import React from 'react';
import { Tooltip } from 'antd';
import { FG140 } from '../../../services/g204Api';
import { Mode } from '../hooks/useG204Mode';
import { isApproved } from '../helpers';

interface G204Permissions {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
  canPrint: boolean;
  canApprove: boolean;
  hasAnyPermission: boolean;
}

interface ActionBarProps {
  mode: Mode;
  isEditing: boolean;
  record: FG140 | null;
  currentIdx: number;
  allIdsLength: number;
  permissions: G204Permissions;
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onPrint: () => void;
  onNavTo: (idx: number) => void;
  onExit: () => void;
}

/** Permission button wrapper */
const PermBtn: React.FC<{
  allowed: boolean;
  disabled: boolean;
  className?: string;
  onClick?: () => void;
  acceptanceAction?: string;
  children: React.ReactNode;
}> = ({ allowed, disabled, className, onClick, acceptanceAction, children }) => {
  const btn = (
    <button
      className={className || 'btn'}
      disabled={disabled || !allowed}
      onClick={onClick}
      data-acceptance-action={acceptanceAction}
    >
      {children}
    </button>
  );
  if (!allowed) {
    return <Tooltip title="權限不足">{btn}</Tooltip>;
  }
  return btn;
};

const ActionBar: React.FC<ActionBarProps> = ({
  mode, isEditing, record,
  currentIdx, allIdsLength,
  permissions,
  onAdd, onModify, onDelete, onPrint, onNavTo, onExit,
}) => {
  const { canView, canCreate, canEdit, canDelete, canPrint } = permissions;

  // Save button: add mode needs canCreate, edit mode needs canEdit
  const canSave = mode === 'add' ? canCreate : canEdit;

  // BR-08: Lock edit/delete when approved
  const locked = isApproved(record);

  return (
    <div className="action-bar">
      {/* Navigation */}
      <div className="action-group">
        <PermBtn allowed={canView} disabled={isEditing || currentIdx <= 0} onClick={() => onNavTo(0)}>最前</PermBtn>
        <PermBtn allowed={canView} disabled={isEditing || currentIdx <= 0} onClick={() => onNavTo(currentIdx - 1)}>上一筆</PermBtn>
        <PermBtn allowed={canView} disabled={isEditing || currentIdx >= allIdsLength - 1} onClick={() => onNavTo(currentIdx + 1)}>下一筆</PermBtn>
        <PermBtn allowed={canView} disabled={isEditing || currentIdx >= allIdsLength - 1} onClick={() => onNavTo(allIdsLength - 1)}>最後</PermBtn>
      </div>

      {/* CRUD */}
      <div className="action-group">
        {/* CMDADD: 新增 / 存檔 */}
        <PermBtn
          acceptanceAction="PWG204-ACT-CREATE"
          allowed={isEditing ? canSave : canCreate}
          disabled={false}
          className="btn btn-primary"
          onClick={onAdd}
        >
          {isEditing ? '存檔' : '新增'}
        </PermBtn>
        {/* CMDMODI: 修改 / 還原 -- disabled when approved (BR-08) */}
        <PermBtn
          acceptanceAction="PWG204-ACT-UPDATE"
          allowed={isEditing ? true : canEdit}
          disabled={(!isEditing && !record) || (!isEditing && locked)}
          onClick={onModify}
        >
          {isEditing ? '還原' : '修改'}
        </PermBtn>
        {/* CMDDEL -- disabled when approved (BR-08) */}
        <PermBtn
          acceptanceAction="PWG204-ACT-DELETE"
          allowed={canDelete}
          disabled={isEditing || !record || locked}
          className="btn btn-danger"
          onClick={onDelete}
        >
          刪除
        </PermBtn>

        <div className="action-separator" />

        {/* 列印 */}
        <PermBtn allowed={canPrint} disabled={isEditing || !record} onClick={onPrint}>列印</PermBtn>

        {/* 離開 */}
        <button className="btn" onClick={onExit}>離開</button>
      </div>

      {/* Status label */}
      {locked && (
        <span style={{
          marginLeft: 12, padding: '2px 10px', borderRadius: 4,
          fontWeight: 'bold', color: '#389e0d',
          background: '#f6ffed', border: '1px solid #b7eb8f',
        }}>
          已核准
        </span>
      )}
    </div>
  );
};

export default React.memo(ActionBar);
